'use client'

import { useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import type { ComponentProps } from 'react'

type ButtonProps = ComponentProps<typeof Button>
import { cn, copyToClipboard } from '@/lib/utils'
import { Copy, Check } from 'lucide-react'

interface CopyButtonProps extends Omit<ButtonProps, 'onClick'> {
  value: string
  onCopySuccess?: () => void
  successMessage?: string
  showText?: boolean
  text?: string
  successText?: string
  iconSize?: number
}

export function CopyButton({
  value,
  onCopySuccess,
  successMessage = 'Copied!',
  showText = false,
  text = 'Copy',
  successText = 'Copied',
  iconSize = 16,
  className,
  variant = 'ghost',
  size = 'icon',
  'aria-label': ariaLabel,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(value)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success(successMessage)
      onCopySuccess?.()
    } else {
      toast.error('Failed to copy', {
        description: 'Please try again',
      })
    }
  }, [value, successMessage, onCopySuccess])

  // Without `showText` this renders a lone SVG, and an icon with no
  // accessible name reaches a screen reader as the word "button" and nothing
  // else. Fall back to the same word the text variant would have shown, so the
  // default is merely terse rather than silent; a call site with more than one
  // copyable value should still pass its own `aria-label`, and doing so wins
  // here. When the text IS shown the label is left off deliberately — an
  // aria-label would override the visible one, which WCAG 2.5.3 forbids
  // diverging from.
  return (
    <Button
      variant={variant}
      size={size}
      className={cn(showText && 'gap-2', className)}
      onClick={handleCopy}
      aria-label={ariaLabel ?? (showText ? undefined : text)}
      {...props}
    >
      {copied ? (
        <>
          <Check
            style={{ width: iconSize, height: iconSize }}
            className="text-green-500"
          />
          {showText && <span>{successText}</span>}
        </>
      ) : (
        <>
          <Copy style={{ width: iconSize, height: iconSize }} />
          {showText && <span>{text}</span>}
        </>
      )}
    </Button>
  )
}

/**
 * Hook for copy functionality with state
 */
export function useCopy(timeout = 2000) {
  const [copied, setCopied] = useState(false)

  const copy = useCallback(
    async (text: string, message?: string) => {
      const success = await copyToClipboard(text)
      if (success) {
        setCopied(true)
        setTimeout(() => setCopied(false), timeout)
        if (message) {
          toast.success(message)
        }
      } else {
        toast.error('Failed to copy', {
          description: 'Please try again',
        })
      }
      return success
    },
    [timeout],
  )

  return { copied, copy }
}
